import type { ReactElement } from 'react';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { ProductCardProps } from '@stitch-fix/mode-react/ProductCard';
import { formatMoney } from '@stitch-fix/format';
import PromoCardSmall, { PROMO_CARD_CONTENT, PROMO_INDEXES } from '../../PromoCardSmall';
import PromoStylistCardSmall, { PROMO_STYLIST_CARD_CONTENT } from '../../PromoStylistCardSmall';
import { ShopProductRecommendationFragment } from '../../PaginatedRecommendations/getUngatedCategoryRecsPaginated';
import RecommendationsGrid from '../RecommendationsGrid';
import { buildUrl } from '../../../utils/buildUrl';
import ProductCardWithEvents from './ProductCardWithEvents';
type ShopProductRecommendationMedia = NonNullable<ShopProductRecommendationFragment['variantBySkuId']>['media'];
const findMediaByContentName = (media: ShopProductRecommendationMedia | undefined, contentName: string) => media?.find(image => image.contentName === contentName && image.originalSrc);

/**
 * We want to show a variety of image types on products grid.
 * We show a detail image immediately following promo cards.
 * For the rest of the images, we show either on figure front, on figure left, or flat images
 */
export const getMediaImage = (shopProduct: ShopProductRecommendationFragment, index: number) => {
  const media = shopProduct?.variantBySkuId?.media;
  let contentName = 'flat_front';
  if (PROMO_INDEXES.includes(index)) {
    contentName = 'figure_detail';
  } else if (index % 2 === 0) {
    contentName = 'figure_front';
  } else if (index % 5 === 0) {
    contentName = 'figure_left';
  }
  return findMediaByContentName(media, contentName) || findMediaByContentName(media, 'flat_front') ||
  // Some products don't have a flat_front image, so we fall back to figure_front if it exists
  findMediaByContentName(media, 'figure_front') ||
  // A last resort fallback is to just use the first image in the media array that has an originalSrc
  // This priority order allows us to keep the grid looking varied while keeping the page from crashing if images are missing
  media?.find(image => image.originalSrc);
};
export const getMediaObjectFromOriginalSrc = (originalSrc: string) => {
  // Create baseUrl and mediaPath from original source url by splitting url at `/upload`
  const uploadPath = '/upload';
  const [baseUrlRoot, mediaPath] = originalSrc.split(uploadPath) || [];
  const baseUrl = `${baseUrlRoot}${uploadPath}`;
  return {
    mediaPath,
    baseUrl
  };
};

/**
 * Takes a shopProduct in a list and builds a recommendation from the shopProduct data.
 * - Adds a Promo card if matches promo index logic
 * - Adds a Stylist Promo if matches index logic
 * - Adds a ProductCard formatted from shopProduct data
 */
const useBuildShopProductRecommendations = ({
  categoryId,
  categorySetId,
  shopProducts
}: {
  categoryId: string;
  categorySetId: string;
  shopProducts: ShopProductRecommendationFragment[];
}) => {
  const recommendations = shopProducts.flatMap((shopProduct, index) => {
    const businessLine = shopProduct?.businessLine;
    const skuId = shopProduct?.variantBySkuId?.id || '';
    const specifiedImage = getMediaImage(shopProduct, index);
    const {
      baseUrl,
      mediaPath
    } = getMediaObjectFromOriginalSrc(specifiedImage?.originalSrc || '');
    const href = shopProduct?.variantBySkuId?.url ? buildUrl(shopProduct?.variantBySkuId?.url, `source=categories&sub_source=${categorySetId}&category_id=${categoryId}`) : '';
    const productCardProps: ProductCardProps = {
      heroMedia: {
        baseUrl,
        mediaPath,
        alt: `${shopProduct?.brand?.name ?? ''} ${shopProduct?.name ?? ''}`.trim(),
        sizes: {
          sm: 320,
          md: 400
        },
        transforms: {
          ar: 1
        }
      },
      heroStageAspectRatio: '4:5',
      href,
      name: shopProduct?.name,
      brand: shopProduct?.brand.name,
      price: shopProduct?.variantBySkuId?.price && formatMoney(shopProduct.variantBySkuId.price.value, {
        inCents: true
      }),
      promoDiscountLabel: shopProduct?.variantBySkuId?.promoDiscountOffer?.displayLabel?.formatted,
      sku: skuId
    };
    const promoContent = PROMO_INDEXES.includes(index) && PROMO_CARD_CONTENT[businessLine][PROMO_INDEXES.indexOf(index)];
    const promoStylistContent = PROMO_STYLIST_CARD_CONTENT[businessLine];
    return [
    // Insert promo card
    promoContent ? <PromoCardSmall key={promoContent.id} id={promoContent.id} name={promoContent.name} actionName={promoContent.actionName} headerText={promoContent.headerText} bodyText={promoContent.bodyText} linkText={promoContent.link.text} linkUrl={promoContent.link.url} /> : null,
    // Insert stylist card
    index === 4 ? <PromoStylistCardSmall key={promoStylistContent.id} {...promoStylistContent} /> : null,
    // Insert sku card
    <ProductCardWithEvents {...productCardProps} key={skuId} categoryId={categoryId} skuId={skuId} />
    // Filter out any undefined items.
    ].filter((recommendation: ReactElement | null): recommendation is ReactElement => !!recommendation);
  });
  return {
    recommendations
  };
};
interface ShopProductCardsProps {
  categoryId: string;
  categorySetId: string;
  shopProducts: ShopProductRecommendationFragment[];
}

/**
 * Renders a list of ProductCards based on the ShopProduct model and in particular the `ShopProductRecommendationFragment`
 * The fragment data is formatted to the ProductCard props and additional customization
 * logic has been applied to select different media images based on the index placement in the list.
 */
const ShopProductCards = ({
  categoryId,
  categorySetId,
  shopProducts
}: ShopProductCardsProps) => {
  const {
    EventProvider,
    ref
  } = useViewEvent({
    category_id: categoryId,
    name: 'grid',
    schema: 'category_view'
  });
  const {
    recommendations
  } = useBuildShopProductRecommendations({
    categoryId,
    categorySetId,
    shopProducts
  });
  return <EventProvider>
      <RecommendationsGrid ref={ref} gridItems={recommendations} />
    </EventProvider>;
};
export default ShopProductCards;